import React, { useState } from 'react';
import { toast } from 'sonner';
import StatusBadge from '../components/ui/StatusBadge';
import { useCampaign } from '../hooks/useCampaign';
import { useAgents } from '../hooks/useAgents';
import { useWAHA } from '../hooks/useWAHA';
import { useSupabaseQuery } from '../hooks/useSupabase';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAppStore } from '../stores/appStore';
import type { ContactList } from '../types';

function LoadingSpinner(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-dx-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const EMPTY_FORM = {
  name: '',
  agent_id: '',
  session_id: '',
  contact_list_id: '',
  messages_per_10min: 5,
  window_start: '10:00',
  window_end: '18:00',
  suggestion: '',
};

type DraftingStep = 'info' | 'suggestion' | 'selection' | 'variants';

export default function ColdLeads(): React.JSX.Element {
  const orgId = useAppStore(s => s.orgId);
  const { campaigns, loading, error, refetch, createCampaign, pauseCampaign } = useCampaign();
  const { agents } = useAgents();
  const { sessions } = useWAHA();

  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep] = useState<DraftingStep>('info');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Drafting states
  const [options, setOptions] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [variants, setVariants] = useState<string[]>([]);

  const { data: contactLists } = useSupabaseQuery<ContactList[]>(
    async () => {
      if (!isSupabaseConfigured) return { data: [], error: null };
      const { data, error: err } = await supabase.from('contact_lists').select('*').order('created_at', { ascending: false });
      return { data: data as ContactList[] | null, error: err };
    },
    []
  );

  const activeAgents = agents.filter(a => a.status === 'active');
  const workingSessions = sessions.filter(s => s.status === 'WORKING');

  const generateOptions = async () => {
    if (!form.suggestion.trim()) { toast.error('Please enter a draft suggestion'); return; }
    setSaving(true);
    try {
      // In a real app, this would call an Edge Function
      // For now, we simulate the 5 options
      const dummyOptions = [
        `Hi {{first_name}}, I saw your work at {{company}} and wanted to connect...`,
        `Hello {{first_name}}! Quick question about your current strategy at {{company}}...`,
        `Greetings from Digitix. We're help companies like {{company}} automate outreach...`,
        `Hi there! Are you the right person to talk to regarding growth at {{company}}?`,
        `Hey {{first_name}}, I have some ideas for {{company}} that might interest you...`
      ];
      setOptions(dummyOptions);
      setStep('selection');
    } catch (err) {
      toast.error('Failed to generate options');
    } finally {
      setSaving(false);
    }
  };

  const generateVariants = async () => {
    if (!selectedOption) return;
    setSaving(true);
    try {
      // Simulate 50 variants
      const dummyVariants = Array.from({ length: 50 }, (_, i) =>
        selectedOption.replace('...', `! (Var ${i + 1})`)
      );
      setVariants(dummyVariants);
      setStep('variants');
    } catch (err) {
      toast.error('Failed to generate variants');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await createCampaign({
        name: form.name,
        status: 'draft',
        agent_id: form.agent_id,
        session_id: form.session_id,
        contact_list_id: form.contact_list_id,
        schedule: {
          start_date: new Date().toISOString().slice(0, 10),
          window_start: form.window_start,
          window_end: form.window_end,
          messages_per_10min: form.messages_per_10min,
          timezone: 'Asia/Baku',
          daily_limit: 500,
          delay_min_seconds: 30,
          delay_max_seconds: 90,
        } as any,
        variants: variants,
        stats: { total_contacts: 0, sent: 0, delivered: 0, read: 0, replied: 0, opted_out: 0, failed: 0 },
      });
      toast.success('Campaign created successfully');
      setShowCreate(false);
      resetFlow();
      refetch();
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const cancelCampaign = async (id: string) => {
    if (!window.confirm('Cancel this campaign and clear queued messages?')) return;
    try {
      await supabase.from('campaigns').update({ status: 'cancelled' }).eq('id', id);
      await supabase.from('campaign_contacts').update({ status: 'skipped' }).eq('campaign_id', id).eq('status', 'pending');
      toast.success('Campaign cancelled');
      refetch();
    } catch (err) {
      toast.error('Failed to cancel campaign');
    }
  };

  const resetFlow = () => {
    setStep('info');
    setForm(EMPTY_FORM);
    setOptions([]);
    setSelectedOption(null);
    setVariants([]);
  };

  const selectClass = "w-full bg-dx-bg border border-dx-border rounded-xl px-3.5 py-2.5 text-[13px] text-dx-text outline-none focus:border-dx-accent transition-colors appearance-none cursor-pointer";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-dx-text m-0">Cold Leads</h2>
          <p className="text-[13px] text-dx-muted mt-1 mb-0">Blast marketing to cold prospects</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 rounded-xl border-none text-[13px] font-semibold text-black bg-[linear-gradient(135deg,#25D366,#1BA350)]">+ New Blast</button>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="flex flex-col gap-3">
          {campaigns.map(c => {
            const stats = c.stats as any;
            const progress = stats.total_contacts > 0 ? Math.round((stats.sent / stats.total_contacts) * 100) : 0;
            return (
              <div key={c.id} className="bg-dx-surface border border-dx-border rounded-2xl p-5 card-hover">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-[15px] font-bold text-dx-text m-0">{c.name}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="text-xs text-dx-dim m-0">
                      {c.schedule.window_start} - {c.schedule.window_end} · {c.schedule.messages_per_10min} msgs / 10m
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {c.status === 'active' && (
                      <button onClick={() => cancelCampaign(c.id)} className="px-3 py-1.5 rounded-lg border border-dx-danger/30 text-dx-danger text-[12px] font-medium hover:bg-dx-danger/10">✕ Cancel</button>
                    )}
                    {c.status === 'draft' && (
                      <button onClick={() => {/* run */ }} className="px-5 py-1.5 rounded-lg border-none text-[12px] font-bold text-black bg-dx-accent hover:opacity-90">▶ Run</button>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between mb-1.5">
                    <span className="text-xs text-dx-muted">Progress: <span className="text-dx-text font-semibold">{stats.sent} sent</span> out of {stats.total_contacts}</span>
                    <span className="text-xs font-bold text-dx-accent">{progress}%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-dx-bg overflow-hidden border border-dx-border/30">
                    <div className="h-full bg-dx-accent shadow-[0_0_8px_rgba(37,211,102,0.4)] transition-all duration-1000" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 pt-2">
                  <div className="text-center">
                    <div className="text-lg font-bold text-dx-text">{stats.sent}</div>
                    <div className="text-[10px] text-dx-dim uppercase tracking-wider">Sent</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-dx-info">{stats.delivered || 0}</div>
                    <div className="text-[10px] text-dx-dim uppercase tracking-wider">Deliv.</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-dx-warning">{stats.read || 0}</div>
                    <div className="text-[10px] text-dx-dim uppercase tracking-wider">Read</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-dx-accent">{stats.replied || 0}</div>
                    <div className="text-[10px] text-dx-dim uppercase tracking-wider">Replied</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Campaign Creation / Drafting Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-auto">
          <div className="bg-dx-surface border border-dx-border rounded-3xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-dx-border flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-dx-text m-0">Setup New Blast</h3>
                <p className="text-xs text-dx-dim m-0">Step {step === 'info' ? 1 : step === 'suggestion' ? 2 : step === 'selection' ? 3 : 4} of 4</p>
              </div>
              <button onClick={() => { setShowCreate(false); resetFlow(); }} className="text-dx-dim hover:text-white">✕</button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {step === 'info' && (
                <div className="flex flex-col gap-5">
                  <div>
                    <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">Blast Name</label>
                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-2xl px-4 py-3 text-[14px] text-dx-text outline-none focus:border-dx-accent" placeholder="e.g. Q1 Real Estate Outreach" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">WhatsApp Session</label>
                      <select value={form.session_id} onChange={e => setForm(f => ({ ...f, session_id: e.target.value }))} className={selectClass}>
                        <option value="">Select session...</option>
                        {workingSessions.map(s => <option key={s.id} value={s.id}>{s.session_name} ({s.phone_number})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">Contact List</label>
                      <select value={form.contact_list_id} onChange={e => setForm(f => ({ ...f, contact_list_id: e.target.value }))} className={selectClass}>
                        <option value="">Select list...</option>
                        {contactLists?.map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_ids.length} contacts)</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">Msgs / 10 min</label>
                      <input type="number" min="1" max="10" value={form.messages_per_10min} onChange={e => setForm(f => ({ ...f, messages_per_10min: parseInt(e.target.value) }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-4 py-2.5 text-[14px] text-dx-text outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">Start Time</label>
                      <input type="time" value={form.window_start} onChange={e => setForm(f => ({ ...f, window_start: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-4 py-2.5 text-[14px] text-dx-text outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-dx-muted mb-1.5 block uppercase tracking-wider font-semibold">End Time</label>
                      <input type="time" value={form.window_end} onChange={e => setForm(f => ({ ...f, window_end: e.target.value }))} className="w-full bg-dx-bg border border-dx-border rounded-xl px-4 py-2.5 text-[14px] text-dx-text outline-none" />
                    </div>
                  </div>
                </div>
              )}

              {step === 'suggestion' && (
                <div className="flex flex-col gap-4">
                  <label className="text-xs text-dx-muted uppercase tracking-wider font-semibold italic">"Tell AI what you want to achieve with this blast..."</label>
                  <textarea value={form.suggestion} onChange={e => setForm(f => ({ ...f, suggestion: e.target.value }))} className="w-full h-40 bg-dx-bg border border-dx-border rounded-2xl p-4 text-[14px] text-dx-text outline-none focus:border-dx-accent resize-none" placeholder="e.g. I want to offer a free audit to dental clinics in Baku. Keep it casual but professional. Use their first name and mention I noticed their website." />
                </div>
              )}

              {step === 'selection' && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-dx-dim mb-2 uppercase tracking-tighter">Choose the best option to use as a base:</p>
                  {options.map((opt, i) => (
                    <div key={i} onClick={() => setSelectedOption(opt)} className={`p-4 rounded-2xl border cursor-pointer transition-all ${selectedOption === opt ? 'bg-dx-accent/10 border-dx-accent' : 'bg-dx-bg/50 border-dx-border hover:border-dx-dim'}`}>
                      <p className="text-[13px] text-dx-text m-0 whitespace-pre-wrap">{opt}</p>
                    </div>
                  ))}
                </div>
              )}

              {step === 'variants' && (
                <div className="flex flex-col gap-4">
                  <div className="p-4 bg-dx-accent/5 rounded-2xl border border-dx-accent/20">
                    <p className="text-xs font-bold text-dx-accent m-0 flex items-center gap-2">✨ AI generated 50 unique variations</p>
                    <p className="text-[11px] text-dx-dim mt-1">These will be used round-robin to avoid WhatsApp spam detection patterns.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2 max-h-60 overflow-auto pr-2">
                    {variants.map((v, i) => (
                      <div key={i} className="p-3 bg-dx-bg/30 border border-dx-border/30 rounded-xl text-[11px] text-dx-muted italic">"{v}"</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-dx-border flex gap-3">
              {step === 'info' && <button onClick={() => setStep('suggestion')} className="flex-1 py-3 rounded-2xl font-bold bg-dx-accent text-black">Next: Ai Suggestion</button>}
              {step === 'suggestion' && (
                <>
                  <button onClick={generateOptions} disabled={saving} className="flex-1 py-3 rounded-2xl font-bold bg-dx-accent text-black">{saving ? 'Generating...' : 'Generate 5 Options'}</button>
                  <button onClick={() => setStep('info')} className="px-6 py-3 rounded-2xl border border-dx-border text-dx-text font-medium">Back</button>
                </>
              )}
              {step === 'selection' && (
                <>
                  <button onClick={generateVariants} disabled={!selectedOption || saving} className="flex-1 py-3 rounded-2xl font-bold bg-dx-accent text-black">{saving ? 'Generating Variants...' : 'Approve & Create 50 Variants'}</button>
                  <button onClick={() => setStep('suggestion')} className="px-6 py-3 rounded-2xl border border-dx-border text-dx-text font-medium">Back</button>
                </>
              )}
              {step === 'variants' && (
                <>
                  <button onClick={handleCreate} disabled={saving} className="flex-1 py-3 rounded-2xl font-bold bg-dx-accent text-black shadow-[0_4px_20px_rgba(37,211,102,0.3)]">{saving ? 'Saving...' : 'Finish & Start Blast'}</button>
                  <button onClick={() => setStep('selection')} className="px-6 py-3 rounded-2xl border border-dx-border text-dx-text font-medium">Back</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
